import type { Instrument, InstrumentId } from "@trader/shared/MarketData"
import { useEffect, useState } from "react"
import { call } from "../../api.ts"

// Instruments rarely change, so each one is fetched once per page load.
const cache = new Map<InstrumentId, Promise<Instrument>>()

export const rememberInstrument = (instrument: Instrument): void => {
  cache.set(instrument.id, Promise.resolve(instrument))
}

const load = (id: InstrumentId): Promise<Instrument> => {
  let pending = cache.get(id)
  if (pending === undefined) {
    pending = call((client) => client.instruments.get({ params: { id } }))
    pending.catch(() => cache.delete(id))
    cache.set(id, pending)
  }
  return pending
}

export type InstrumentState =
  | { readonly status: "loading" }
  | { readonly status: "ready"; readonly instrument: Instrument }
  | { readonly status: "failed"; readonly error: unknown }

export const useInstrument = (id: InstrumentId): InstrumentState => {
  const [state, setState] = useState<InstrumentState>({ status: "loading" })
  useEffect(() => {
    let current = true
    setState({ status: "loading" })
    load(id).then(
      (instrument) => current && setState({ status: "ready", instrument }),
      (error: unknown) => current && setState({ status: "failed", error })
    )
    return () => {
      current = false
    }
  }, [id])
  return state
}
